import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import BaseOverviewStore, {
    type ApiResponse,
    type BaseStoreState,
} from '../../../shared/stores/baseOverviewStore';
import { formatName } from '../../../utils/helper';
import { MatenRequestResponse } from '../../../shared';

interface ObjectStoreState extends BaseStoreState {
    includeArchived: boolean;
    filterManager: any;
}

/**
 * Load all objects and make it possible to manage them
 */
class ObjectOverviewStore extends BaseOverviewStore<any> {
    protected storageKey(): string {
        return 'object';
    }

    // Implement required abstract methods
    protected defaultSortField(): string {
        return 'object_number';
    }

    protected getApiEndpoint(): string {
        return 'object';
    }

    protected getApiPath(): string {
        return 'query';
    }

    // Object-specific properties
    public filterType: string | null = null;
    public includeArchived: boolean = false;
    public available_managers: Array<{ id: number; name: string }> = [];
    public filterManager: any = null;

    constructor(sdk: any) {
        super(sdk);

        // Make object-specific properties observable
        makeObservable(this, {
            filterType: observable,
            includeArchived: observable,
            available_managers: observable,
            filterManager: observable,
            setIncludeArchived: action,
            applyFilterManager: action,
            fetchAvailableManagers: action,
            deleteObject: action,
            allObjects: computed,
        });

        // Re-load from storage now that observables are set up
        this.loadFromLocalStorage();
    }

    protected buildApiParams(page: number): any {
        return {
            sortvalue: this.sortField,
            archived: this.includeArchived,
            sort: this.sortOrder === 1 ? 'asc' : 'desc',
            search: this.searchValue || '',
            page,
            ...(this.filterManager
                ? { filter: { type: 'manager', value: this.filterManager.id } }
                : {}),
        };
    }

    protected processApiResponse(response: ApiResponse): any[] {
        return (response.json.data || []).map((item: any) => ({
            ...item,
            manager: item.manager
                ? {
                      ...item.manager,
                      name: formatName(item.manager.firstname, item.manager.lastname),
                  }
                : item.manager,
        }));
    }

    protected getStorageState(): ObjectStoreState {
        return {
            sortOrder: this.sortOrder,
            sortField: this.sortField,
            searchValue: this.searchValue,
            includeArchived: this.includeArchived,
            filterManager: this.filterManager,
        };
    }

    protected applyStorageState(state: ObjectStoreState): void {
        runInAction(() => {
            this.sortOrder = state.sortOrder || 1;
            this.sortField = state.sortField || this.defaultSortField();
            this.searchValue = state.searchValue || '';
            this.includeArchived = state.includeArchived || false;
            this.filterManager = state.filterManager || null;
        });
    }

    // Object-specific methods
    public setIncludeArchived(value: boolean): void {
        this.includeArchived = value;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public applyFilterManager(manager: any): void {
        this.filterManager = manager;
        this.saveToLocalStorage();
        this.fetchList();
    }

    public async fetchAvailableManagers(force: boolean = false): Promise<void> {
        if (!force && this.available_managers.length > 1) {
            return;
        }
        const managerResponse: any = await this.sdk.fetchApi('manager', 'query', {});

        runInAction(() => {
            if (managerResponse?.status === 200) {
                this.available_managers = managerResponse.json.data.map((manager: any) => ({
                    id: manager.id,
                    name: formatName(manager.firstname, manager.lastname),
                }));
            }
        });
    }

    public async deleteObject(object_id: number): Promise<MatenRequestResponse> {
        if (object_id) {
            try {
                const responseQuestion = await this.sdk.fetchApi('object', 'delete', {
                    object_id,
                });

                if (responseQuestion.status === 200) {
                    this.fetchList(this.latestPage);
                    return { success: true, status: 200, data: responseQuestion.json };
                }

                const errorCode = responseQuestion?.json?.error || '';

                if (errorCode) {
                    switch (errorCode) {
                        case 'ERR_OBJECT_NOT_ARCHIVED':
                            throw new Error(
                                'Das Objekt kann nur gelöscht werden, wenn es archiviert ist.'
                            );
                        case 'ERR_OBJECT_NOT_FOUND':
                            throw new Error('Das Objekt wurde nicht gefunden.');
                        default:
                            break;
                    }
                }

                throw new Error('Ein unbekannter Fehler ist aufgetreten');
            } catch (error: any) {
                return {
                    success: false,
                    status: 500,
                    error: error.message || 'Ein unbekannter Fehler ist aufgetreten',
                };
            }
        }
        return { success: false, status: 404, error: 'Objekt nicht gefunden' };
    }

    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (dialog === 'object' && event?.event === 'SAVED') {
            await this.fetchList(this.latestPage);
        } else {
            await super.onDialogLeave(dialog, event);
        }
    }

    // Computed properties and aliases for compatibility
    get allObjects(): Array<any> | null {
        if (!this.list) return null;
        return this.list.map((item: any) => ({
            ...item,
            address: `${item.address.street} ${item.address.housenumber}, ${item.address.zipcode} ${item.address.city}`,
        }));
    }

    get objectList(): Array<any> {
        return this.list;
    }

    get objectListLoading(): boolean {
        return this.listLoading;
    }

    public fetchObjectList(page: number = 1): Promise<void> {
        return this.fetchList(page);
    }
}

export default ObjectOverviewStore;
